import React, { useState, useEffect, useMemo } from 'react';

/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values */
import { Box } from '@atlaskit/primitives/compiled/box';
import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import {
	useCommentsPanelState,
	AddFooterCommentMode,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { getWidthAndMaxWidth } from '@confluence/comments-util/entry-points/domUtils.ts';
import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import {
	useObjectCommentDispatchContext,
	useObjectCommentContext,
} from '@confluence/comment-context/entry-points/ObjectCommentContext.ts';
import { usePageContentId } from '@confluence/page-context';
import { usePageRecommendationData } from '@confluence/page-recommendations/entry-points/usePageRecommendationData.tsx';

import { usePageContentDimensions } from '../hooks/usePageContentDimensions.ts';
import { hideWhiteSpace } from '../utils/hideWhiteSpace.ts';

import { DraftCommentBox } from './DraftCommentBox.tsx';
import { ObjectAddCommentProfileWrapper } from './ObjectAddCommentProfileWrapper.tsx';

const styles = cssMap({
	// View: fixed + hideWhiteSpace() insets — sticky + left/right/width fights the layout engine and
	// leaves the bar off-column or off-screen when the footer scrolls away.
	stickyDraftViewPageWrapper: {
		position: 'sticky',
		zIndex: 1,
	},
	stickyDraftLivePageWrapper: {
		position: 'fixed',
		zIndex: 500,
	},
	hiddenStyles: {
		display: 'none',
	},
	visibleStyles: {
		display: 'flex',
	},
	commonStyle: {
		alignItems: 'center',
		justifyContent: 'center',
		bottom: token('space.0'),
		backgroundColor: token('elevation.surface'),
	},
	childWrapperStyle: {
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderColor: token('color.border'),
		padding: token('space.200'),
		margin: '0 auto',
	},
	zIndexStyle: {
		zIndex: 200,
		left: token('space.0'),
		width: '100%',
	},
});

export type StickyDraftCommentContainerProps = {
	pageWidthType?: string;
};

export const StickyDraftCommentContainer: React.FC<StickyDraftCommentContainerProps> = ({
	pageWidthType,
}) => {
	const { expanded, isObjectCommentWrapperVisible, footerEditorEntry } = useObjectCommentContext();
	const {
		expandCommentContainer,
		setWrapperWidth,
		setIsObjectCommentWrapperVisible,
		setFooterEditorEntry,
	} = useObjectCommentDispatchContext();
	const [contentId] = usePageContentId();
	const [objectCommentWrapperDimensions, setObjectCommentWrapperDimensions] = useState<{
		width: number;
	} | null>(null);

	const pageMode = useGetPageMode();

	const isViewPage = pageMode === PageMode.VIEW;

	// Track page-content-only dimensions for layout alignment and blue background sizing
	const {
		width: pageContentWidth,
		left: pageContentLeft,
		right: pageContentRight,
	} = usePageContentDimensions();

	const { hasRecs } = usePageRecommendationData();

	const { addFooterCommentAdf } = useCommentsPanelState();

	// Hide the sticky draft when the comment wrapper is visible so that when they scroll back up they don't have the full editor visible
	useEffect(() => {
		if (isObjectCommentWrapperVisible && expanded) {
			expandCommentContainer();
			setFooterEditorEntry(AddFooterCommentMode.FOOTER);
		}
	}, [isObjectCommentWrapperVisible, expandCommentContainer, expanded, setFooterEditorEntry]);

	useEffect(() => {
		let intersectionObserver: IntersectionObserver | null = null;
		let resizeObserver: ResizeObserver | null = null;
		// Only tracked (and only ever differs from `commentWrapper`) when the gate below is on.
		let observedWrapper: Element | null = null;

		const commentWrapperSelector = '[data-testid="object-comment-wrapper"]';

		const calculateObjectCommentWrapperWidth = () => {
			const objectCommentWrapper = document.querySelector(commentWrapperSelector);
			if (objectCommentWrapper) {
				const rect = objectCommentWrapper.getBoundingClientRect();
				setObjectCommentWrapperDimensions({
					width: rect.width,
				});
				setWrapperWidth(rect.width);
			}
		};

		// Two-stage hydration can swap in a new `object-comment-wrapper` node; without re-attaching,
		// the observers are left watching a detached element and the sticky draft bar never hides
		// once the real wrapper becomes visible. Gated so gate-off keeps master's "attach once" below.
		const setupObservers = () => {
			const commentWrapper = document.querySelector(commentWrapperSelector);

			if (!commentWrapper) {
				setIsObjectCommentWrapperVisible(false);
				return;
			}

			if (
				observedWrapper &&
				observedWrapper !== commentWrapper &&
				UNSAFE_expValEqualsNoExposure('confluence_footer_reactions_redesign', 'isEnabled', true)
			) {
				intersectionObserver?.disconnect();
				resizeObserver?.disconnect();
				intersectionObserver = null;
				resizeObserver = null;
			}
			observedWrapper = commentWrapper;

			if (!intersectionObserver) {
				intersectionObserver = new IntersectionObserver(
					([entry]) => setIsObjectCommentWrapperVisible(entry.isIntersecting),
					{ rootMargin: '0px' },
				);
				intersectionObserver.observe(commentWrapper);
			}

			if (!resizeObserver) {
				resizeObserver = new ResizeObserver(calculateObjectCommentWrapperWidth);
				resizeObserver.observe(commentWrapper);
			}

			calculateObjectCommentWrapperWidth();
		};

		setupObservers();

		const mutationObserver = new MutationObserver(() => {
			const commentWrapper = document.querySelector(commentWrapperSelector);
			if (
				commentWrapper &&
				(!intersectionObserver ||
					(!isViewPage && !resizeObserver) ||
					(commentWrapper !== observedWrapper &&
						UNSAFE_expValEqualsNoExposure(
							'confluence_footer_reactions_redesign',
							'isEnabled',
							true,
						)))
			) {
				setupObservers();
			}
		});

		mutationObserver.observe(document.body, {
			childList: true,
			subtree: true,
		});

		return () => {
			if (intersectionObserver) {
				intersectionObserver.disconnect();
			}
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
			mutationObserver.disconnect();
		};
	}, [isViewPage, setWrapperWidth, setIsObjectCommentWrapperVisible, contentId]);

	const pageContentDimensions = useMemo(
		() => ({
			width: pageContentWidth,
			left: pageContentLeft,
			right: pageContentRight,
		}),
		[pageContentWidth, pageContentLeft, pageContentRight],
	);

	const getStyle = () => {
		if (isViewPage) {
			const computedWidths = getWidthAndMaxWidth({
				pageMode,
				pageWidthType,
				hasEndOfPageRecommendations: hasRecs ?? false,
			});
			return {
				width: computedWidths.width,
				maxWidth: computedWidths.maxWidth,
			};
		}
		return {
			maxWidth: '100%',
			width: objectCommentWrapperDimensions ? `${objectCommentWrapperDimensions.width}px` : '100%',
		};
	};

	// Show the sticky draft if the comment wrapper is not visible and there is an ADF or the editor is expanded
	// This is to prevent the draft comment from disappearing if you delete all of the text
	const showStickyDraft =
		!isObjectCommentWrapperVisible &&
		(addFooterCommentAdf || expanded) &&
		footerEditorEntry !== AddFooterCommentMode.STICKY_DRAFT;

	const expandEditorHandler = () => {
		expandCommentContainer();
		setFooterEditorEntry(AddFooterCommentMode.STICKY_DRAFT);
	};

	if (!showStickyDraft) {
		return;
	}
	// Use hidden / visible styles below to prevent unnecessary unmounting (which can have side effects, specifically for editor)
	return (
		<Box
			xcss={cx(
				isViewPage ? styles.stickyDraftViewPageWrapper : styles.stickyDraftLivePageWrapper,
				showStickyDraft ? styles.visibleStyles : styles.hiddenStyles,
				styles.commonStyle,
				!isViewPage && styles.zIndexStyle,
			)}
			testId="sticky-draft-comment-box"
			id="addCommentButton-draft"
			style={
				pageMode === PageMode.VIEW
					? hideWhiteSpace({
							shouldRenderFixedStyle: true,
							pageContentDimensions,
						})
					: undefined
			}
		>
			<Box xcss={styles.childWrapperStyle} style={getStyle()}>
				<Box>
					<ObjectAddCommentProfileWrapper>
						<Box xcss={cx(!showStickyDraft ? styles.hiddenStyles : styles.visibleStyles)}>
							<DraftCommentBox expandPageCommentEditorHandler={expandEditorHandler} />
						</Box>
					</ObjectAddCommentProfileWrapper>
				</Box>
			</Box>
		</Box>
	);
};
